import React from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { InfoBanner } from '~/editor/ui/InfoBanner';
import ScalesWhiteIcon from '~/assets/editor/scales-white.svg';
import WhiteLockIcon from '~/assets/icons/white_lock.svg';
import { LEFT_MENU_WIDTH, DISCLAIMER_MARGIN } from '~/editor/constants';
import { isTemplateSupportedForEditing } from '~/training/sequence/templates/utils/templateSupport';
import { COMPLIANCE_CONTENT_TOOLTIP_INDEX } from '~/editor/tooltips/tourConstants';

const BannerContainer = styled.div<{ $showMenu: boolean }>`
  position: fixed;
  bottom: ${DISCLAIMER_MARGIN}px;
  left: ${props =>
    props.$showMenu
      ? `${LEFT_MENU_WIDTH + DISCLAIMER_MARGIN}px`
      : `${DISCLAIMER_MARGIN}px`};
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: left 0.2s ease;
`;

interface EditorBannersProps {
  showMenu: boolean;
}

export const EditorBanners: React.FC<EditorBannersProps> = ({ showMenu }) => {
  const { sequence, training, showTooltips, tooltip } = useEditorStore();

  const isLegallyRequired = React.useMemo(() => {
    if (!training || !sequence) return false;

    for (const chapter of training.chapters) {
      const chapterSequence = chapter.chapterSequences?.find(
        (cs: { sequence?: { id?: unknown; legallyRequired?: boolean } }) =>
          cs?.sequence?.id === sequence.id
      );
      if (chapterSequence?.sequence?.legallyRequired) {
        return true;
      }
    }

    return false;
  }, [training, sequence]);

  const showLegallyRequiredDuringComplianceTour =
    training?.customizationOfCanonical === true &&
    showTooltips &&
    tooltip === COMPLIANCE_CONTENT_TOOLTIP_INDEX;

  const showLegallyRequiredBanner =
    isLegallyRequired || showLegallyRequiredDuringComplianceTour;

  const isTemplateUnsupported = React.useMemo(
    () =>
      sequence?.editable !== false && !isTemplateSupportedForEditing(sequence),
    [sequence]
  );
  const isShared = sequence?.editable === false;

  if (!showLegallyRequiredBanner && !isTemplateUnsupported && !isShared) {
    return null;
  }

  return (
    <BannerContainer $showMenu={showMenu}>
      {isShared && (
        <InfoBanner
          icon={<WhiteLockIcon />}
          title="Shared slide"
          subtitle="This slide’s content is shared across courses and is read-only here."
          testId="shared-slide-disclaimer"
        />
      )}
      {showLegallyRequiredBanner && (
        <InfoBanner
          icon={<ScalesWhiteIcon />}
          title="Legally required content"
          subtitle="Changes may affect compliance"
          testId="legally-required-disclaimer"
        />
      )}
      {isTemplateUnsupported && (
        <InfoBanner
          icon={<WhiteLockIcon />}
          title="Slide changes not supported"
          subtitle="This slide doesn’t support content edits. You can keep it as-is, delete it, or copy and paste the text content into a new slide to make changes there."
          testId="unsupported-template-disclaimer"
        />
      )}
    </BannerContainer>
  );
};
